/** @jsxImportSource @emotion/react */
import { Connector } from "wagmi";
import { ReactComponent as GeneicWalletIcon } from "../../../../img/navigation/wallet.svg";
import { ReactComponent as CoinbaseIcon } from "../../../../img/wallets/Coinbase.svg";
import { ReactComponent as MetamaskIcon } from "../../../../img/wallets/Metamask.svg";
import { ReactComponent as WalletConnectIcon } from "../../../../img/wallets/WalletConnect.svg";

import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useMemo } from "react";

interface WalletIconProps {
  connector: Connector | undefined;
  options?: StylingOptions;
}

export const WalletIcon = ({ connector, options }: WalletIconProps) => {
  const size = options?.size ?? 14;
  const borderRadius = options?.borderRadius ?? "14px";
  const cssBorderRadius = useMemo(
    () => css`
      border-radius: ${borderRadius};
    `,
    [borderRadius]
  );

  if (
    connector &&
    (connector.name === "WalletConnectLegacy" || connector.name === "WalletConnect")
  ) {
    return <WalletConnectIcon width={size} height={size} css={cssBorderRadius} />;
  }

  const InjectedWalletIcon =
    INJECTED_PROVIDER_ICON_MAP[connector?.name as InjectedProviders] ??
    INJECTED_PROVIDER_ICON_MAP.Undetermined;
  const withBackground = options?.withBackground && connector?.name === "MetaMask" ? true : false;

  if (withBackground) {
    const withBackgroundIconSize = Math.floor(size * 0.75);
    return (
      <Background size={size} borderRadius={borderRadius}>
        <InjectedWalletIcon width={withBackgroundIconSize} height={withBackgroundIconSize} />
      </Background>
    );
  }

  return <InjectedWalletIcon width={size} height={size} css={cssBorderRadius} />;
};

// Helpers

const enum InjectedProviders {
  "MetaMask" = "MetaMask",
  "Coinbase Wallet" = "Coinbase Wallet",
}

export const INJECTED_PROVIDER_ICON_MAP = {
  MetaMask: MetamaskIcon,
  "Coinbase Wallet": CoinbaseIcon,
  Undetermined: GeneicWalletIcon,
};

interface StylingOptions {
  size?: number;
  withBackground: boolean;
  borderRadius?: number | string;
}

// Sub-components

interface BackgroundProps {
  size: StylingOptions["size"];
  borderRadius?: StylingOptions["borderRadius"];
}

const Background = styled.div<BackgroundProps>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: ${props =>
    typeof props.borderRadius === "number" ? `${props.borderRadius}px` : props.borderRadius};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(254, 237, 214);
`;
